import React, { useState, useMemo } from 'react';
import type { Rally, SpottedEvent } from '@/api/resultsApi';
import { formatTime } from '@/utils/formatTime';
import EventMarker from './EventMarker';

interface RallyTimelineProps {
  /** Elenco dei rally con eventi */
  rallies: Rally[];
  /** Durata totale del video in millisecondi */
  durationMs: number;
  /** Tempo corrente di riproduzione (ms) */
  currentTimeMs?: number;
  /** Callback al click su un rally */
  onRallyClick?: (rally: Rally) => void;
  /** Callback al click su un evento */
  onEventClick?: (event: SpottedEvent) => void;
}

/**
 * Timeline visiva dei rally con indicatori di eventi.
 * Ogni rally è rappresentato da un segmento colorato proporzionale alla durata.
 * Un cursore rosso indica la posizione corrente del player.
 */
const RallyTimeline: React.FC<RallyTimelineProps> = ({
  rallies,
  durationMs,
  currentTimeMs = 0,
  onRallyClick,
  onEventClick,
}) => {
  const [hoveredRallyId, setHoveredRallyId] = useState<string | null>(null);

  if (durationMs <= 0) return null;

  const currentPercent = Math.min(100, (currentTimeMs / durationMs) * 100);

  const teamAColor = '#3b82f6';
  const teamBColor = '#ef4444';
  const neutralColor = '#94a3b8';

  return (
    <div style={{ margin: '0.5rem 0', position: 'relative', width: '100%' }}>
      {/* Barra di sfondo */}
      <div
        style={{
          width: '100%',
          height: '28px',
          backgroundColor: '#e2e8f0',
          borderRadius: '6px',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {/* Segmenti rally */}
        {rallies.map((rally) => {
          const left = (rally.start_timestamp_ms / durationMs) * 100;
          const rallyDuration = rally.end_timestamp_ms - rally.start_timestamp_ms;
          const width = Math.max(0.3, (rallyDuration / durationMs) * 100);
          const isHovered = hoveredRallyId === rally.rally_id;
          const winningTeam = rally.winning_team;
          const rallyColor =
            winningTeam === 'A' ? teamAColor
            : winningTeam === 'B' ? teamBColor
            : neutralColor;

          return (
            <div
              key={rally.rally_id}
              style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                height: '100%',
                backgroundColor: isHovered ? rallyColor : `${rallyColor}99`,
                border: isHovered ? `2px solid ${rallyColor}` : '1px solid transparent',
                borderRadius: '4px',
                cursor: onRallyClick ? 'pointer' : 'default',
                zIndex: isHovered ? 10 : 1,
                transition: 'background-color 0.2s, border 0.2s',
                overflow: 'visible',
              }}
              onMouseEnter={() => setHoveredRallyId(rally.rally_id)}
              onMouseLeave={() => setHoveredRallyId(null)}
              onClick={() => onRallyClick?.(rally)}
            >
              {/* Marcatori eventi all'interno del segmento */}
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {rally.events.map((event, idx) => {
                  const relativeLeft =
                    rallyDuration > 0
                      ? ((event.timestamp_ms - rally.start_timestamp_ms) / rallyDuration) * 100
                      : 50;
                  const verticalOffset = ((idx % 3) - 1) * 2; // distribuisce verticalmente

                  return (
                    <EventMarker
                      key={event.event_id}
                      event={event}
                      onClick={onEventClick}
                      style={{
                        left: `${relativeLeft}%`,
                        top: `${verticalOffset}px`,
                      }}
                    />
                  );
                })}
              </div>

              {/* Tooltip rally */}
              {isHovered && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '34px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    padding: '0.3rem 0.6rem',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 20,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  {formatTime(rally.start_timestamp_ms)} → {formatTime(rally.end_timestamp_ms)} &nbsp;|&nbsp;
                  {rally.score.A}-{rally.score.B} (Team {rally.winning_team})
                </div>
              )}
            </div>
          );
        })}

        {/* Indicatore tempo corrente */}
        <div
          style={{
            position: 'absolute',
            left: `${currentPercent}%`,
            top: '-4px',
            height: '36px',
            width: '2px',
            backgroundColor: '#ef4444',
            zIndex: 15,
            boxShadow: '0 0 4px rgba(239,68,68,0.6)',
            transition: 'left 0.1s linear',
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
};

export default RallyTimeline;